import { Radio } from "@base-ui/react/radio";
import { RadioGroup as BaseRadioGroup } from "@base-ui/react/radio-group";
import checkIcon from "@loot-os/icons/icons/check";
import type { IconData } from "@loot-os/icons/types";
import { type ReactNode, useId } from "react";
import { cn, tv } from "../foundations/cn.js";
import { FOCUS_RING } from "../foundations/interaction.js";
import { useImageFallback } from "../foundations/useImageFallback.js";
import { Field } from "./Field.js";
import { Icon } from "./Icon.js";

export interface RadioOption<Value extends string> {
  value: Value;
  /** Images: the picture's name, read by a screen reader and shown nowhere. */
  label: ReactNode;
  /** A line under the label: what choosing it means. */
  description?: ReactNode;
  /** Tiles only: an icon above the label. */
  icon?: IconData;
  /** Something at the end of the row or tile: a price, "Free". */
  end?: ReactNode;
  /** Images only: the picture's URL. */
  image?: string;
  disabled?: boolean;
}

export interface RadioGroupProps<Value extends string> {
  /** The question the group answers, shown above it and used as its name. */
  label: string;
  hideLabel?: boolean;
  helper?: string;
  error?: string;
  options: readonly RadioOption<Value>[];
  value?: Value;
  defaultValue?: Value;
  onValueChange?: (value: Value) => void;
  /**
   * `list`: a radio and its label per row. `tiles`: each option on a card the size of a tap.
   * `images`: a grid of square pictures, one to choose (a box's art); each option's `label`
   * names its picture.
   */
  appearance?: "list" | "tiles" | "images";
  /** Tiles per row from sm; phones always show one. Images fill the row at about 80px each. */
  columns?: 1 | 2 | 3;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  className?: string;
}

const optionStyles = tv({
  base: [
    "group flex cursor-pointer gap-3 font-body text-text-secondary has-data-disabled:cursor-default has-data-disabled:text-text-disabled",
  ],
  variants: {
    appearance: {
      list: "items-start py-1",
      tiles: [
        "relative items-start rounded-field border border-border-default bg-bg-surface p-4",
        "not-has-data-disabled:hover:border-border-strong has-data-checked:border-accent-default has-data-checked:bg-accent-subtle",
        "has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-solid has-focus-visible:outline-focus-ring",
      ],
    },
  },
});

/** The circle, for a list row or a tile's corner. */
const DOT = cn(
  "mt-0.5 flex size-4.5 shrink-0 items-center justify-center rounded-pill border border-border-input bg-bg-inset",
  "hover:bg-bg-hover data-checked:border-accent-default data-checked:bg-accent-default data-disabled:opacity-40",
);

const COLUMNS = { 1: "", 2: "sm:grid-cols-2", 3: "sm:grid-cols-3" } as const;

const GROUP_LAYOUT = {
  list: "flex flex-col gap-2",
  tiles: "grid gap-3",
  images: "grid grid-cols-[repeat(auto-fill,minmax(4.5rem,1fr))] gap-2",
} as const;

/** One picture to choose: the accent edge and a check while chosen. */
const IMAGE_OPTION = cn(
  "relative aspect-square cursor-pointer overflow-hidden rounded-lg border-2 border-transparent bg-bg-inset p-1.5",
  "not-data-disabled:hover:border-border-strong data-checked:border-accent-default data-disabled:cursor-default data-disabled:opacity-40",
  FOCUS_RING,
);

/**
 * One choice from a short list, all of it visible: radios in rows, tiles, or pictures. One Tab
 * stop; the arrow keys move and choose. For a filter over a list, ChipRadioGroup; for switching views,
 * SegmentedControl.
 */
export function RadioGroup<Value extends string>({
  label,
  hideLabel,
  helper,
  error,
  options,
  value,
  defaultValue,
  onValueChange,
  appearance = "list",
  columns = 1,
  disabled,
  required,
  name,
  className,
}: RadioGroupProps<Value>) {
  const baseId = useId();
  return (
    <Field
      kind="group"
      label={label}
      hideLabel={hideLabel}
      helper={helper}
      error={error}
      className={className}
    >
      {(field) => (
        <BaseRadioGroup
          aria-labelledby={field.labelId}
          aria-describedby={field.describedBy}
          aria-invalid={field.invalid || undefined}
          value={value}
          defaultValue={defaultValue}
          onValueChange={(next) => onValueChange?.(next as Value)}
          disabled={disabled}
          name={name}
          className={cn(GROUP_LAYOUT[appearance], appearance === "tiles" && COLUMNS[columns])}
        >
          {options.map((option) => {
            if (appearance === "images") {
              return (
                <Radio.Root
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  required={required}
                  aria-label={typeof option.label === "string" ? option.label : undefined}
                  className={IMAGE_OPTION}
                >
                  <OptionImage src={option.image} label={option.label} />
                  <Radio.Indicator className="absolute top-1 right-1 flex size-5 items-center justify-center rounded-pill bg-accent-default text-text-on-accent">
                    <Icon icon={checkIcon} size={12} />
                  </Radio.Indicator>
                </Radio.Root>
              );
            }
            const descriptionId = `${baseId}-${option.value}-description`;
            return (
              // biome-ignore lint/a11y/noLabelWithoutControl: Base UI's radio root is the control inside it.
              <label key={option.value} className={optionStyles({ appearance })}>
                <Radio.Root
                  value={option.value}
                  disabled={option.disabled}
                  required={required}
                  aria-describedby={option.description ? descriptionId : undefined}
                  className={cn(
                    DOT,
                    appearance === "list" && FOCUS_RING,
                    appearance === "tiles" && "order-last outline-hidden",
                  )}
                >
                  <Radio.Indicator className="size-2 rounded-pill bg-text-on-accent" />
                </Radio.Root>
                <span className="flex min-w-0 flex-1 flex-col gap-1">
                  {appearance === "tiles" && option.icon && (
                    <Icon icon={option.icon} size={20} className="mb-1 text-text-primary" />
                  )}
                  <span className="flex items-baseline justify-between gap-3">
                    <span
                      className={cn(
                        "group-has-data-disabled:text-text-disabled",
                        // A bare row reads like a Checkbox beside it; a described one or a tile leads with its label.
                        appearance === "list" && !option.description
                          ? "text-body-b2 text-text-secondary"
                          : "text-body-b1-strong text-text-primary",
                      )}
                    >
                      {option.label}
                    </span>
                    {option.end && (
                      <span className="shrink-0 text-body-b2 text-text-secondary">
                        {option.end}
                      </span>
                    )}
                  </span>
                  {option.description && (
                    <span id={descriptionId} className="text-body-b2 text-text-secondary">
                      {option.description}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </BaseRadioGroup>
      )}
    </Field>
  );
}

/**
 * An image choice's picture, or its label in the picture's place when the image is missing or
 * fails, so a choice never shows as an empty square.
 */
function OptionImage({ src, label }: { src: string | undefined; label: ReactNode }) {
  const { failed, onError } = useImageFallback(src);
  if (failed || !src) {
    return (
      <span className="flex size-full items-center justify-center px-1 text-center font-body text-body-b3 text-text-secondary">
        {label}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt=""
      draggable={false}
      onError={onError}
      className="size-full object-contain"
    />
  );
}
